import { motion } from "framer-motion";
import { PageSEO } from "@/components/PageSEO";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Privacy Policy"
        description="Your-IT-Solutions privacy policy. Learn how we collect, use, and protect your personal data in accordance with UK GDPR."
        path="/privacy"
      />
      <section className="py-20 lg:py-28 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">Legal</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Privacy <span className="gradient-text">Policy</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose-custom space-y-10">
            <p className="text-gray-400 leading-relaxed">
              We are committed to protecting your personal data in accordance with UK GDPR.
            </p>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">1. Who We Are</h2>
              <p className="text-gray-400 leading-relaxed">
                Your-IT-Solutions provides IT services, website design, hosting, and business technology solutions.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">2. What Data We Collect</h2>
              <p className="text-gray-400 mb-3">We may collect:</p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Name and contact details (via forms or enquiries)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Business information
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Usage data (via analytics tools)
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Data</h2>
              <p className="text-gray-400 mb-3">We use your data to:</p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Respond to enquiries
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Provide services
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Improve our website and offerings
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">4. Data Storage</h2>
              <p className="text-gray-400 leading-relaxed">
                Your data is stored securely using trusted providers (e.g. Microsoft 365, CRM systems).
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">5. Sharing Data</h2>
              <p className="text-gray-400 mb-3">We do not sell your data. We may share it with:</p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Hosting providers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Payment processors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  CRM systems
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">6. Your Rights</h2>
              <p className="text-gray-400 mb-3">You have the right to:</p>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Access your data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Request correction or deletion
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1.5 block w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Withdraw consent
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3">7. Contact</h2>
              <p className="text-gray-400 leading-relaxed">
                For data requests, contact:{" "}
                <a href="mailto:contact@your-it-solutions.com" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                  contact@your-it-solutions.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
